'use client';

import { Checkbox } from '@base-ui/react/checkbox';
import clsx from 'clsx';

import { DataTestIds } from '@repo/frontend-shared';

import '@repo/sds/components/SDSCheckbox/SDSCheckbox.scss';

type SDSCheckboxMode = 'inactive' | 'active' | 'subcheck';

export interface SDSCheckboxProps {
  isChecked: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  onChange?: (checked: boolean) => void;
  mode?: SDSCheckboxMode;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  dataTestId?: string;
}

export function SDSCheckbox({
  isChecked,
  isDisabled = false,
  isError = false,
  onChange,
  mode = 'active',
  className,
  id,
  name,
  value,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  dataTestId,
}: SDSCheckboxProps) {
  const checkboxClass = clsx(
    'SDSCheckbox',
    mode && `SDSCheckbox--${mode}`,
    {
      'SDSCheckbox--checked': isChecked,
      'SDSCheckbox--disabled': isDisabled,
      'SDSCheckbox--error': isError,
      [`SDSCheckbox--${mode}${isError ? '--error' : ''}`]: isError && mode !== 'inactive',
    },
    className,
  );

  const handleCheckedChange = (checked: boolean) => {
    if (!isDisabled && onChange) {
      onChange(checked);
    }
  };

  return (
    <div className={checkboxClass}>
      <Checkbox.Root
        className="SDSCheckbox__root"
        checked={isChecked}
        disabled={isDisabled}
        onCheckedChange={handleCheckedChange}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        data-testid={dataTestId || DataTestIds.SDS.SDS_CHECKBOX}
        name={name}
        value={value}
        id={id}
      >
        <Checkbox.Indicator className="SDSCheckbox__indicator">
          {isChecked && <div className="SDSCheckbox__icon">{mode === 'subcheck' ? <MinusIcon /> : <TickIcon />}</div>}
        </Checkbox.Indicator>
      </Checkbox.Root>
    </div>
  );
}

// Icon components (inline SVGs for simplicity)
function TickIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <title>Checkmark</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.402 5.22C16.74 5.54 16.76 6.07 16.44 6.4L8.66 14.74C8.51 14.9 8.29 15 8.06 15C7.82 15 7.6 14.9 7.45 14.74L3.56 10.57C3.24 10.23 3.26 9.7 3.6 9.39C3.93 9.08 4.46 9.09 4.78 9.43L8.06 12.95L15.22 5.26C15.54 4.93 16.07 4.91 16.402 5.22Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <title>Minus sign</title>
      <path
        d="M4 10C4 9.45 4.38 9 4.86 9H15.14C15.62 9 16 9.45 16 10C16 10.55 15.62 11 15.14 11H4.86C4.38 11 4 10.55 4 10Z"
        fill="currentColor"
      />
    </svg>
  );
}
